import React, { useState } from 'react';
import Select, { StylesConfig } from 'react-select';
import { Font } from 'src/design-system/text/font';
import { Button } from 'src/design-system/button/Button';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import styles from './pre-contact-form.module.scss';
import { ButtonRemake } from '@utmb/design-system/button-remake/ButtonRemake';
import { useGlobal } from '@utmb/contexts/GlobalContext';
import { useTranslation } from 'next-i18next';

type CategoryId = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';

interface Category {
    id: CategoryId;
    label: string;
}

interface RaceOption {
    value: string;
    label: string;
    url: string;
}

const WORLDSERIES_URL = '/contact-worldseries';

const CATEGORIES_WITH_RACE: CategoryId[] = ['A', 'B', 'C'];

const selectStyles: StylesConfig<RaceOption> = {
    control: (base, state) => ({
        ...base,
        background: 'transparent',
        borderColor: state.isFocused ? 'var(--utmb-color-white)' : 'var(--utmb-color-border-darked-gray)',
        borderRadius: '6px',
        minHeight: '52px',
        boxShadow: 'none',
        cursor: 'text',
        '&:hover': {
            borderColor: 'var(--utmb-color-white)',
        },
    }),
    menu: (base) => ({
        ...base,
        background: 'var(--utmb-color-primary)',
        border: '1px solid var(--utmb-color-border-darked-gray)',
        borderRadius: '6px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
    }),
    option: (base, state) => ({
        ...base,
        background: state.isFocused ? 'var(--utmb-color-border-darked-gray)' : 'transparent',
        color: 'var(--utmb-color-white)',
        cursor: 'pointer',
        '&:active': {
            background: 'var(--utmb-color-border-darked-gray)',
        },
    }),
    singleValue: (base) => ({
        ...base,
        color: 'var(--utmb-color-white)',
    }),
    placeholder: (base) => ({
        ...base,
        color: 'var(--utmb-color-border-gray)',
    }),
    input: (base) => ({
        ...base,
        color: 'var(--utmb-color-white)',
    }),
    clearIndicator: (base) => ({
        ...base,
        color: 'var(--utmb-color-border-darked-gray)',
        cursor: 'pointer',
        '&:hover': { color: 'var(--utmb-color-white)' },
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: 'var(--utmb-color-border-darked-gray)',
        cursor: 'pointer',
        '&:hover': { color: 'var(--utmb-color-white)' },
    }),
    indicatorSeparator: (base) => ({
        ...base,
        backgroundColor: 'var(--utmb-color-border-darked-gray)',
    }),
    noOptionsMessage: (base) => ({
        ...base,
        color: 'var(--utmb-color-border-gray)',
    }),
};

export const PreContactForm = (): React.ReactElement => {
    const { t } = useTranslation();

    const CATEGORIES: Category[] = [
        { id: 'A', label: t('preContactForm.option.a') },
        { id: 'B', label: t('preContactForm.option.b') },
        { id: 'C', label: t('preContactForm.option.c') },
        { id: 'D', label: t('preContactForm.option.d') },
        { id: 'E', label: t('preContactForm.option.e') },
        { id: 'F', label: t('preContactForm.option.f') },
    ];

    const [step, setStep] = useState<1 | 2>(1);
    const [isExiting, setIsExiting] = useState(false);
    const [nextStep, setNextStep] = useState<1 | 2>(1);
    const [selectedRace, setSelectedRace] = useState<RaceOption | null>(null);

    const {
        global: { eventsTopBar },
    } = useGlobal();

    const raceOptions: RaceOption[] = (eventsTopBar ?? []).map((event) => ({
        value: event.url,
        label: event.title,
        url: `${event.url}/contact`,
    }));

    const navigateTo = (to: 1 | 2): void => {
        if (isExiting) return;
        setNextStep(to);
        setIsExiting(true);
    };

    const handleExitEnd = (): void => {
        setStep(nextStep);
        setIsExiting(false);
    };

    const handleCategorySelect = (id: CategoryId): void => {
        if (CATEGORIES_WITH_RACE.includes(id)) {
            navigateTo(2);
        } else {
            window.location.href = WORLDSERIES_URL;
        }
    };

    const handleBack = (): void => {
        setSelectedRace(null);
        navigateTo(1);
    };

    const handleConfirm = (): void => {
        if (selectedRace) {
            window.location.href = selectedRace.url;
        }
    };

    const stepClass = (isExiting: boolean): string => `${styles.step} ${isExiting ? styles.stepExiting : styles.stepEntering}`;

    return (
        <div className={styles.wrapper}>
            <div className={styles.stepsContainer}>
                {/* ── Step 1 : Choix du motif ── */}
                {step === 1 && (
                    <div className={stepClass(isExiting)} onAnimationEnd={isExiting ? handleExitEnd : undefined}>
                        <Font.FuturaBold as="h2" mobile="22" desktop="34" className={styles.question}>
                            {t('preContactForm.title')}
                        </Font.FuturaBold>

                        <ul className={styles.categories}>
                            {CATEGORIES.map((cat) => (
                                <li key={cat.id}>
                                    <button className={styles.categoryBtn} onClick={() => handleCategorySelect(cat.id)}>
                                        <span className={styles.categoryKey}>{cat.id}</span>
                                        <Font.FuturaBook as="span" mobile="14" desktop="16" className={styles.categoryLabel}>
                                            {cat.label}
                                        </Font.FuturaBook>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* ── Step 2 : Sélection de la course ── */}
                {step === 2 && (
                    <div className={stepClass(isExiting)} onAnimationEnd={isExiting ? handleExitEnd : undefined}>
                        <Font.FuturaBold as="h2" mobile="22" desktop="34" className={styles.question}>
                            {t('preContactForm.race.title')}
                        </Font.FuturaBold>

                        <div className={styles.selectWrapper}>
                            <Select<RaceOption>
                                options={raceOptions}
                                value={selectedRace}
                                onChange={(option) => setSelectedRace(option)}
                                placeholder={t('preContactForm.race.placeholder')}
                                isClearable
                                noOptionsMessage={() => t('preContactForm.race.noRacesFound')}
                                styles={selectStyles}
                                instanceId="pre-contact-race"
                            />
                        </div>

                        <div className={styles.actions}>
                            <ButtonRemake variant="link" label={t('preContactForm.button.back')} action={handleBack} />
                            <Button.Default
                                bgcolor={getColorStyle(ColorTypesEnum.SECONDARY)}
                                label={t('preContactForm.button.next')}
                                onPress={handleConfirm}
                                disabled={!selectedRace}
                                color={getColorStyle(ColorTypesEnum.PRIMARY)}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
